import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useDisplayUnits from 'hooks/useDisplayUnits';
import {
  CustomUnitFields,
  DisplayUnits,
  FillType,
  MetricPressureUnit,
  MixerCoefficientsDto,
  ModelNodeDto,
  PipeFrictionDataDto,
  PourDto,
  PumpType,
  ThroughputControlType,
  UcsCoefficientsDto,
  UnitSystem
} from 'providers/api';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import PourHydraulicFillDetails from './PourHydraulicFillDetails';
import PourPasteFillDetails from './PourPasteFillDetails';
import UnitSystemDetail from './ProjectAreaPage/ProjectStopesPage/StopePanelPage/RecipesTab/UnitSystemDetail';
import WarningTooltipIcon from './ProjectAreaPage/ProjectStopesPage/StopePanelPage/WarningTooltipIcon';
import { RoutePoint } from './ProjectAreaPage/ProjectStopesPage/MineModel/types';

const PREFIX = 'PourDetails';

const classes = {
  table: `${PREFIX}-table`,
};

const Root = styled('div')(() => ({
  [`& .${classes.table}`]: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      fontSize: '0.9em',
      padding: 10,
      margin: 0,
      paddingBottom: 10,
    },
  },
}));

interface PourCardProps {
  pour: PourDto;
  displayUnitPreferences: DisplayUnits[];
  unitSystemPreference: UnitSystem;
  specificGravity?: number;
  yieldStressA?: number;
  yieldStressB?: number;
  mixerCoefficients?: MixerCoefficientsDto;
  heightOfCylinder?: number;
  fillType: FillType;
  throughputControlType: ThroughputControlType;
  tailingsSolidsDensity?: number;
  binderParticleDensity?: number;
  carrierFluidDensity?: number;
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto; };
  customUnitFields: CustomUnitFields[];
  backFillPlantNodeId: string | undefined;
  pipeFrictionDataItems?: PipeFrictionDataDto[];
  pumpType: PumpType;
  mineModelNodes?: Pick<ModelNodeDto, 'nodeId' | 'boosterPressure'>[];
  routePoints?: RoutePoint[];
  metricPressureUnit?: MetricPressureUnit;
}

const PourDetails = ({
  pour,
  displayUnitPreferences,
  unitSystemPreference,
  specificGravity,
  yieldStressA,
  yieldStressB,
  mixerCoefficients,
  heightOfCylinder,
  fillType,
  throughputControlType,
  tailingsSolidsDensity,
  binderParticleDensity,
  carrierFluidDensity,
  targetDaysCoefficients,
  customUnitFields,
  backFillPlantNodeId,
  pipeFrictionDataItems,
  pumpType,
  mineModelNodes,
  routePoints,
  metricPressureUnit,
}: PourCardProps) => {
  const displayUnitResults = useDisplayUnits([{
    key: 'adjusted',
    displayUnitPreferences,
    specification: fillType === FillType.Paste ? pour.pasteSpecification : pour.hydraulicSpecification,
    selectedSpecification: fillType === FillType.Paste ? pour.pasteSelectedSpecification : pour.hydraulicSelectedSpecification,
    specificGravity,
    yieldStressA,
    yieldStressB,
    mixerCoefficients,
    heightOfCylinder,
    fillType,
    throughputControlType,
    tailingsSolidsDensity,
    binderParticleDensity,
    carrierFluidDensity,
  },
  {
    key: 'optimised',
    displayUnitPreferences,
    specification: fillType === FillType.Paste ? pour.pasteSpecification : pour.hydraulicSpecification,
    selectedSpecification: fillType === FillType.Paste ? pour.pasteOptimisedSpecification : pour.hydraulicOptimisedSpecification,
    specificGravity,
    yieldStressA,
    yieldStressB,
    mixerCoefficients,
    heightOfCylinder,
    fillType,
    throughputControlType,
    tailingsSolidsDensity,
    binderParticleDensity,
    carrierFluidDensity,
  }]);

  const { additionalDisplayUnits: additionalAdjustedDisplayUnitMap, warnings } = displayUnitResults.adjusted;
  const { additionalDisplayUnits: additionalOptimisedDisplayUnitMap } = displayUnitResults.optimised;

  const processedWarnings = Object.entries(warnings).filter(([/* key */, value]) => value !== undefined).map(([/* key */, value]) => ({ display: value! }));

  return (
    <Root>
      {processedWarnings && processedWarnings.length > 0 && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
            mb: 1,
          }}
        >
          <WarningTooltipIcon warnings={processedWarnings as any} />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          pl: 2,
        }}
      >
        <Typography variant="caption"><Trans id="Rheology Data:">Rheology Data: </Trans></Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          mb: 1,
          pl: 2,
        }}
      >
        <Typography variant="caption">{`${pour.rheologyDataSetReference} - ${pour.rheologyDataSetVersion}`}</Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          pl: 2,
        }}
      >
        <Typography variant="caption"><Trans id="Backfill Plant:">Backfill Plant: </Trans></Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          mb: 1,
          pl: 2,
        }}
      >
        <Typography variant="caption">
          {`Node ${backFillPlantNodeId || ''}`}
        </Typography>
      </Box>
      <Table className={classes.table} aria-label={i18n._('Pour details table')} size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right"><Trans id="Design">Design</Trans></TableCell>
            <TableCell align="right"><Trans id="Selected">Selected</Trans></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            fillType === FillType.Paste
              ? (
                <PourPasteFillDetails
                  pour={pour}
                  unitSystemPreference={unitSystemPreference}
                  throughputControlType={throughputControlType}
                  targetDaysCoefficients={targetDaysCoefficients}
                  customUnitFields={customUnitFields}
                  pumpType={pumpType}
                  mineModelNodes={mineModelNodes}
                  routePoints={routePoints}
                  pipeFrictionDataItems={pipeFrictionDataItems}
                  displayUnitPreferences={displayUnitPreferences}
                  metricPressureUnit={metricPressureUnit}
                />
              )
              : (
                <PourHydraulicFillDetails
                  pour={pour}
                  unitSystemPreference={unitSystemPreference}
                  throughputControlType={throughputControlType}
                  customUnitFields={customUnitFields}
                />
              )
          }
          <TableRow>
            <TableCell colSpan={3}><Divider /></TableCell>
          </TableRow>
          {
            Object.entries(additionalAdjustedDisplayUnitMap)
              .filter(([/* key */, unitInfo]) => unitInfo.value !== undefined)
              .map(([key, unitInfo]) => (
                <UnitSystemDetail
                  key={key}
                  designed={(additionalOptimisedDisplayUnitMap as any)[key].value}
                  selected={unitInfo.value}
                  unitSystem={unitSystemPreference}
                  unitInfo={unitInfo}
                  decimalPlaces={unitInfo.decimalPlaces}
                  customUnitFields={customUnitFields}
                />
              ))
          }
        </TableBody>
      </Table>
    </Root>
  );
};

export default PourDetails;
